import Modal from '@/components/Modal';
import { Table, TableRow, TBody, TD, TH, THead } from '@/components/DesignLibrary/Table';
import { useState } from 'react';
import { Checkbox } from '@/components/DesignLibrary/Atoms';
import Button from '@/components/DesignLibrary/Button';
import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { useEventsContext } from '@/providers/Events';
import { Questionnaire } from '@/typings/Questionnaire';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import type { DeletedRow } from './List';

import getDisplayValue from './getDisplayValue';
import s from './List.module.scss';

type ModalProps = {
  setRemovedItemsModalOpen: (val: boolean) => void;
  fullField: Questionnaire.Field;
  deletedValues: DeletedRow[];
  questionnaireId: string;
  isEditor?: boolean;
  fieldsAutofilling?: string[];
  setValue: (arg1: { key: string, value: unknown }) => void;
  formMethods: UseFormReturn<FieldValues>;
  linkSourceFieldKey: string;
};

const DeletedFieldsModal: React.FC<ModalProps> = ({
  setRemovedItemsModalOpen,
  fullField,
  deletedValues,
  questionnaireId,
  formMethods,
  setValue,
  linkSourceFieldKey,
}) => {
  const [checkedRows, setCheckedRows] = useState<Array<string>>([]);
  const { emit } = useEventsContext();

  const { getData: restoreDeletedQuestionnaireResponsesFn } = useNoodleApi(tsClient.questionnaires.restoreDeletedQuestionnaireResponses);

  const handleCheckboxChange = (rowId: string): void => {
    const found = checkedRows.find(id => id === rowId);
    if (found) setCheckedRows(checkedRows.filter(id => id !== rowId));
    else setCheckedRows([...checkedRows, rowId]);
  };

  const handleRestoreItems = async (): Promise<void> => {
    await restoreDeletedQuestionnaireResponsesFn({ listFieldTemplateKey: linkSourceFieldKey, questionnaireId, subFieldTemplateIds: checkedRows });
    const restoredItems = deletedValues.filter(value => checkedRows.includes(value._id));
    const existingValues = formMethods.getValues()[linkSourceFieldKey];
    setValue({ key: linkSourceFieldKey, value: [...existingValues, ...restoredItems] });

    emit('questionnaireResponseRestored', {});
  };

  return (
    <Modal
      title={'Removed Items'}
      isWide
      onClose={() => {
        setRemovedItemsModalOpen(false);
      }}
    >
      <div className={s.modal}>
        <Table className={s.table}>
          <THead>
            <TableRow>
              <TH />
              {(fullField.listItemTemplates ?? [])
                .filter(subField => subField.isViewableInTableView)
                .map(subField => (
                  <TH key={subField.id}>
                    <span className="caption" style={{ color: 'var(--color-gray-75)', textAlign: 'left' }}>
                      {subField.label}
                    </span>
                  </TH>
                ))}
            </TableRow>
          </THead>
          <TBody>
            {deletedValues.map((row, rowIndex) => (
              <TableRow key={row._id}>
                {
                  <>
                    <TD className={s.editButtonCell}>
                      <div className={s.editButtonCellBg} />
                      <Checkbox
                        size="sm"
                        label=""
                        isChecked={checkedRows.includes(row._id)}
                        onChange={() => handleCheckboxChange(row._id)}
                        className={s.editCheckbox}
                      />
                    </TD>
                    {(fullField.listItemTemplates ?? [])
                      .filter(column => column.isViewableInTableView)
                      .map((column, columnIndex) => (
                        <TD key={`${rowIndex}-${columnIndex}`}>
                          <span className="caption" style={row[column.key] ? undefined : { color: 'var(--color-gray-25)' }}>
                            {getDisplayValue(column, row[column.key])}
                          </span>
                        </TD>
                      ))}
                  </>
                }
              </TableRow>
            ))}
          </TBody>
        </Table>
        <Button
          variant="primary"
          size="md"
          onClick={() => {
            setRemovedItemsModalOpen(false);
            handleRestoreItems();
          }}
          disabled={checkedRows.length === 0}
        >
          Restore Items
        </Button>
      </div>
    </Modal>
  );
};

export default DeletedFieldsModal;
